import type { Map as MapLibreMap } from "maplibre-gl";
import { AGE_SCALE, ageInk } from "@/listings/listing.freshness.ts";

/**
 * Price pills, rendered as complete images — background, ring and the price
 * itself in one bitmap, generated on demand per label.
 *
 * The price used to be MapLibre text stretched over a nine-patch icon
 * (`icon-text-fit`), and that split is exactly what made overlapping pills
 * ugly: a symbol layer paints every icon in one pass and every text in
 * another, so whatever the draw order says, an overlapped pill's price prints
 * *across* the pill above it rather than clipping under it. No sort key or
 * collision setting can fix that — the two passes exist below the style. One
 * baked image makes each pill atomic: pills clip over each other like stacked
 * cards, every visible pill carries its own price, and a price can never land
 * on a pill it doesn't belong to.
 *
 * Images are created lazily through `styleimagemissing`: the layer's
 * `icon-image` expression concats the pill's state with its `priceLabel`, and
 * the first time MapLibre asks for a combination we haven't drawn, we draw it.
 * Distinct labels are rents rounded to $100s, so the working set stays small.
 *
 * A pill carries three facts at once, on three separate channels so they never
 * have to compete:
 *
 * - **age** is the fill, from AGE_SCALE (and the ink follows it — see ageInk);
 * - **tracked** replaces that fill with one colour belonging to nothing else on
 *   the map, because a shortlist is worth spotting from across a full viewport;
 * - **unopened** and **open right now** are rings around the outside, baked in
 *   for the same one-object reason as the price.
 */

/** A tracked listing's fill. Violet: nothing else on the map is anywhere near
 *  it, so it reads as a different kind of statement rather than as an age. */
export const TRACKED = "#a78bfa";

type Ring = "none" | "unopened" | "open";

/** The canvas height, sized for the ring. The pill sits inside, inset by GAP. */
const H = 28;
const GAP = 3;
const PILL_H = H - GAP * 2;
const PILL_R = PILL_H / 2;
const RING_R = H / 2;
/** Text inset from the pill's edge. */
const PAD = 7;
const SCALE = 3; // rendered at 3× so the pills stay sharp on retina displays
/** Matches the 14px Noto Sans Bold the map used to typeset these with. */
const FONT = '700 14px "Noto Sans", "Helvetica Neue", Arial, sans-serif';

const RING_SUFFIX: Record<Ring, string> = { none: "", unopened: "-new", open: "-onst" };

/** State id for one age stop; the full image id appends `|<priceLabel>`. */
const pillStateId = (index: number, ring: Ring): string => `pill-${index}${RING_SUFFIX[ring]}`;
/** State id for a tracked listing, whose fill no longer depends on its age. */
const trackedStateId = (ring: Ring): string => `pill-track${RING_SUFFIX[ring]}`;

function stepByAge(id: (index: number) => string): unknown[] {
  const expr: unknown[] = ["step", ["get", "ageDays"], id(0)];
  for (let i = 0; i < AGE_SCALE.length - 1; i++) {
    expr.push(AGE_SCALE[i].maxDays, id(i + 1));
  }
  return expr;
}

/** Fill by tracked-ness, then by age — for one ring state. */
function byFill(ring: Ring): unknown[] {
  return ["case", ["get", "tracked"], trackedStateId(ring), stepByAge((i) => pillStateId(i, ring))];
}

/**
 * `icon-image` for a pill, by state — `<stateId>|<priceLabel>`, resolved to a
 * baked image by the styleimagemissing handler addPillImages wires up.
 *
 * The unopened ring marks what still wants looking at, not what has been looked
 * at. An unopened listing carries the extra ornament and catches the eye across
 * a crowded map; opening it takes the ring away, so the map quietens as you work
 * through it and whatever is left standing out is whatever is left to do.
 *
 * Precedence is open → tracked → unopened. Tracked outranks unopened because a
 * shortlisted listing is one you have already read; the ring would be answering
 * a question you settled when you marked it.
 */
export function pillImageExpression(selectedId: string | null): unknown[] {
  const resting: unknown[] = [
    "case",
    ["get", "tracked"],
    trackedStateId("none"),
    ["!", ["get", "seen"]],
    stepByAge((i) => pillStateId(i, "unopened")),
    stepByAge((i) => pillStateId(i, "none")),
  ];
  const state =
    selectedId === null
      ? resting
      : // The open one wins outright: it keeps a ring, in marine, even though
        // opening it is exactly what has just retired its own.
        ["case", ["==", ["get", "id"], selectedId], byFill("open"), ...resting.slice(1)];
  return ["concat", state, "|", ["get", "priceLabel"]];
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

interface Palette {
  marine: string;
  outline: string;
}

/** The ring colours each map was last given — the one theme-dependent part. */
const palettes = new WeakMap<MapLibreMap, Palette>();
/** Maps whose styleimagemissing handler is already attached. */
const wired = new WeakSet<MapLibreMap>();
/** Every image id this module has drawn into a map, for theme-swap redraws. */
const drawn = new WeakMap<MapLibreMap, Set<string>>();

/**
 * Register the palette and the on-demand renderer. Idempotent — safe after a
 * style swap, which wipes the style's images but not the map's event handlers:
 * the next frame simply re-raises styleimagemissing for whatever it needs.
 *
 * `outline` is the unopened ring; `marine` is the open one. When either
 * changes (a theme flip), every image already in the style is redrawn in
 * place — `hasImage` alone would happily keep serving the old rings.
 */
export function addPillImages(
  map: MapLibreMap,
  { marine, outline }: { marine: string; outline: string },
): void {
  const prev = palettes.get(map);
  palettes.set(map, { marine, outline });

  if (!wired.has(map)) {
    wired.add(map);
    map.on("styleimagemissing", (e: { id: string }) => {
      if (e.id.startsWith("pill-")) renderPill(map, e.id);
    });
  }

  if (prev && (prev.outline !== outline || prev.marine !== marine)) {
    for (const id of drawn.get(map) ?? []) {
      if (map.hasImage(id)) renderPill(map, id);
    }
  }
}

/** `pill-<track|ageIndex>[-new|-onst]|<label>`, parsed back into paint inputs. */
const PILL_ID = /^pill-(track|\d+)(-new|-onst)?\|(.*)$/;

/** The age band `ageDays` falls in — the JS mirror of stepByAge. */
function bandIndex(ageDays: number): number {
  const i = AGE_SCALE.findIndex((s) => ageDays < s.maxDays);
  return i === -1 ? AGE_SCALE.length - 1 : i;
}

/**
 * Draw every image the given features will resolve to, before the tiles that
 * reference them are built.
 *
 * This is belt-and-braces against maplibre#8064: a tile whose symbols point at
 * a not-yet-added image can come out of the worker with a corrupt feature
 * index, and every queryRenderedFeatures through it then throws. Handing the
 * style a complete image set up front means `styleimagemissing` (kept as a
 * fallback) has nothing left to do in the normal path.
 */
export function ensurePillImages(
  map: MapLibreMap,
  features: {
    properties: { ageDays: number; seen: boolean; tracked: boolean; priceLabel: string };
  }[],
): void {
  for (const f of features) {
    const p = f.properties;
    const state = p.tracked
      ? trackedStateId("none")
      : pillStateId(bandIndex(p.ageDays), p.seen ? "none" : "unopened");
    const id = `${state}|${p.priceLabel}`;
    if (!map.hasImage(id)) renderPill(map, id);
  }
}

/** The marine-ringed variant one listing wears while its card is open. */
export function ensureOpenPillImage(
  map: MapLibreMap,
  { ageDays, tracked, label }: { ageDays: number; tracked: boolean; label: string },
): void {
  const state = tracked ? trackedStateId("open") : pillStateId(bandIndex(ageDays), "open");
  const id = `${state}|${label}`;
  if (!map.hasImage(id)) renderPill(map, id);
}

/** Half the rendered pill's height, for screen-space hit-testing. */
export const PILL_HIT_HALF_H = H / 2;

/** Half the rendered width of the pill carrying `label`, for the same. */
export function pillHitHalfWidth(label: string): number {
  return (Math.max(PILL_H, Math.ceil(labelWidth(label)) + PAD * 2) + GAP * 2) / 2;
}

let measure: CanvasRenderingContext2D | null = null;
function labelWidth(label: string): number {
  measure ??= document.createElement("canvas").getContext("2d");
  if (!measure) return 36;
  measure.font = FONT;
  return measure.measureText(label).width;
}

function renderPill(map: MapLibreMap, id: string): void {
  const palette = palettes.get(map);
  const m = PILL_ID.exec(id);
  if (!palette || !m) return;

  const [, fillKey, ringKey, label] = m;
  const fill =
    fillKey === "track"
      ? TRACKED
      : (AGE_SCALE[Number(fillKey)]?.color ?? AGE_SCALE[AGE_SCALE.length - 1].color);
  const ring = ringKey === "-new" ? palette.outline : ringKey === "-onst" ? palette.marine : null;

  // The pill grows to its label; the caps stay true semicircles either way.
  const pillW = Math.max(PILL_H, Math.ceil(labelWidth(label)) + PAD * 2);
  const w = pillW + GAP * 2;

  const canvas = document.createElement("canvas");
  canvas.width = w * SCALE;
  canvas.height = H * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(SCALE, SCALE);

  if (ring) {
    ctx.strokeStyle = ring;
    ctx.lineWidth = 2;
    roundedRect(ctx, 1, 1, w - 2, H - 2, RING_R - 1);
    ctx.stroke();
  }

  // A hairline casing keeps the pill legible where it overlaps the coastline or
  // a bright park fill, without needing a text halo.
  ctx.fillStyle = "#04070a";
  roundedRect(ctx, GAP, GAP, pillW, PILL_H, PILL_R);
  ctx.fill();
  ctx.fillStyle = fill;
  roundedRect(ctx, GAP + 1, GAP + 1, pillW - 2, PILL_H - 2, PILL_R - 1);
  ctx.fill();

  // The ink follows the fill (see ageInk) — near-black on the bright young
  // stops, chalk on the slate old ones.
  ctx.font = FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = ageInk(fill);
  ctx.fillText(label, w / 2, H / 2 + 0.5);

  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const image = { width: img.width, height: img.height, data: new Uint8Array(img.data.buffer) };
  // Remove-then-add, never updateImage: updateImage demands identical
  // dimensions, and a pill's width can legitimately shift a pixel between
  // draws (font metrics settle once the page's fonts finish loading). The
  // styleimagemissing handler runs inside MapLibre's render pass, so a throw
  // here doesn't just lose one icon — it kills the whole frame.
  if (map.hasImage(id)) map.removeImage(id);
  map.addImage(id, image, { pixelRatio: SCALE });

  let ids = drawn.get(map);
  if (!ids) drawn.set(map, (ids = new Set()));
  ids.add(id);
}
